import React from "react"
import { Field as FieldPrimitive } from "@base-ui/react/field"
import { cn } from "../utils"
import { FieldContext, useFieldContext } from "./field-context"

// =============================================================================
// Styles
// =============================================================================
const itemStyles = [
  // The control takes the first column and the text the second, so a
  // description lines up under its label rather than under the control.
  // `content-start` keeps the rows at their natural height: an item whose box is
  // taller than its content (equal-height cards in a row) would otherwise share
  // the spare space out between the rows and push the description off its label.
  "grid grid-cols-[auto_1fr] content-start items-start gap-x-2",
  "[&>[data-slot=field-label]]:col-start-2",
  "[&>[data-slot=field-description]]:col-start-2",
  // The item greys its own label off its own control, rather than leaving it
  // to the field: a field holding many items greys one option at a time.
  "has-data-disabled:[&_[data-slot=field-label]]:text-gray-600",
  "has-data-disabled:[&_[data-slot=field-description]]:text-gray-600",
  // The label operates the control, so it takes the control's cursor.
  "[&_[data-slot=field-label]]:cursor-pointer",
  "has-data-disabled:[&_[data-slot=field-label]]:cursor-not-allowed"
]

// =============================================================================
// Props
// =============================================================================
export type FieldItemProps = {
  /** A control, its `Field.Label` and optionally its `Field.Description` */
  children?: React.ReactNode
  /**
   * Disables this option's `Checkbox` or `Radio` and greys its label, leaving
   * the rest of the group alone. Those are the two controls that read the
   * item's state; any other control has to be disabled at the call site.
   *
   * `disabled` on the surrounding `Field` wins: an item cannot re-enable a
   * control the field has disabled.
   */
  disabled?: boolean
  /**
   * Replaces the item's `<div>` with another element, which then carries the
   * item's layout and state instead of nesting inside it.
   */
  render?: FieldPrimitive.Item.Props["render"]
  /** Additional CSS classes for layout */
  className?: string
}

// =============================================================================
// Component
// =============================================================================
/**
 * One option inside a `Field` that holds a group of controls.
 *
 * Each item scopes its own label, description, id and disabled state, so a
 * radio or checkbox group is a single `Field` — one name, one validation
 * state — rather than one field per option.
 *
 * The control sits beside its label, which is the only layout a group option
 * has.
 *
 * Must be rendered inside a `Field`. On its own it throws
 * `FieldRootContext is missing`, so a group is never just a `RadioGroup`.
 */
export const FieldItem = React.forwardRef<HTMLDivElement, FieldItemProps>(
  function FieldItem({ children, disabled, render, className }, ref) {
    const field = useFieldContext()
    const labelId = React.useId()

    return (
      <FieldContext.Provider value={{ ...field, labelId }}>
        <FieldPrimitive.Item
          ref={ref}
          data-slot="field-item"
          disabled={disabled}
          render={render}
          className={cn(itemStyles, className)}
        >
          {children}
        </FieldPrimitive.Item>
      </FieldContext.Provider>
    )
  }
)

FieldItem.displayName = "Field.Item"
